/**
 * ChatLandingPage - Empty state for users without club context
 *
 * Shown when user navigates to /chat without being in a club.
 * Provides friendly guidance to join a club to access chat.
 *
 * Issue #809: Graceful handling when user has no clubs
 * Issue #2222: A clubs FETCH FAILURE must not masquerade as "you have no
 * clubs" — a member with clubs on a flaky connection was told to go get
 * invited. Error branch mirrors native's discuss tab (error + retry), and
 * a resolved NON-EMPTY club list routes to the club's chat instead of
 * re-rendering the join landing (review P2 on PR #2273).
 */

import { useNavigate, Navigate } from 'react-router-dom';
import { MessageSquare, AlertTriangle } from 'lucide-react';
import { TopBar, EmptyState, ClubCardSkeleton } from '../components';
import { useClubs } from '../hooks/useClubs';
import { useClubNavContext } from '../hooks/useClubNavContext';

export default function ChatLandingPage() {
  const navigate = useNavigate();
  const { data: clubs, isLoading, error, refetch } = useClubs();
  // Canonical club resolver (validated stored slug → primary club) — the
  // same hook BottomNav routes Discuss with, so a recovery here lands where
  // the tab would have gone.
  const clubSlug = useClubNavContext();

  // Clubs resolved non-empty (initial load, retry recovery, or cached data
  // behind a background error): this user can chat — route them there.
  if (clubs && clubs.length > 0 && clubSlug) {
    return <Navigate to={`/clubs/${clubSlug}/chat`} replace />;
  }

  let body: React.ReactNode;
  if (isLoading) {
    // Clubs still resolving — don't flash the no-clubs landing.
    body = (
      <div className="w-full max-w-md">
        <ClubCardSkeleton />
      </div>
    );
  } else if (error && !clubs) {
    // Fetch failed with nothing cached: error + retry, NOT the join landing.
    body = (
      <EmptyState
        icon={<AlertTriangle className="w-12 h-12" />}
        title="Couldn't load your clubs"
        message="Check your connection and try again."
        actionLabel="Retry"
        onAction={() => void refetch()}
      />
    );
  } else {
    body = (
      <EmptyState
        icon={<MessageSquare className="w-12 h-12" />}
        title="Join a club to chat"
        message="Club chats are where members connect. Get invited to a club or create your own to start chatting."
        actionLabel="Create Club"
        onAction={() => navigate('/clubs/create')}
      />
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Discuss" />
      <div className="flex-1 pt-14 flex items-center justify-center px-4">{body}</div>
    </div>
  );
}
